import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { API_BASE_URL } from '../constants';
import { validAdminResponse } from '../utils/admin';

// Admin reads are cancellable and never turn an error into an empty successful page.
export function useAdminRead<T>(path: string | null, method: 'GET' | 'POST' = 'GET') {
  const { apiKey } = useAuth();
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ data: T | null; loadedPath: string | null; loadedKey: string | null; loading: boolean; error: string | null }>({
    data: null, loadedPath: null, loadedKey: null, loading: !!path, error: null,
  });
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!path) { setState({ data: null, loadedPath: null, loadedKey: null, loading: false, error: null }); return; }
    if (!apiKey) {
      setState({ data: null, loadedPath: null, loadedKey: null, loading: false, error: 'Sign in with an admin account.' });
      return;
    }
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 20000);
    setState(previous => ({ ...previous, data: previous.loadedPath === path && previous.loadedKey === apiKey ? previous.data : null, loading: true, error: null }));
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${path}`, {
          method, headers: { Authorization: `Bearer ${apiKey}` }, signal: controller.signal,
        });
        const body: unknown = await response.json().catch(() => null);
        if (!response.ok) {
          const detail = body && typeof body === 'object' && 'detail' in body ? body.detail : null;
          throw new Error(typeof detail === 'string' ? detail : `Request failed (${response.status}). Try again or narrow the filters.`);
        }
        if (!validAdminResponse(path, body)) throw new Error('The server returned an invalid response.');
        if (!controller.signal.aborted) setState({ data: body as T, loadedPath: path, loadedKey: apiKey, loading: false, error: null });
      } catch (error) {
        if (!controller.signal.aborted || timedOut) setState(previous => ({ ...previous, loading: false,
          error: timedOut ? 'The request took too long. Retry or narrow the filters.' : error instanceof Error ? error.message : 'Could not load this view.' }));
      } finally { clearTimeout(timer); }
    })();
    return () => { clearTimeout(timer); controller.abort(); };
  }, [apiKey, path, method, revision]);
  return { ...state, stale: state.loadedPath !== path, refresh };
}
